/**
 * Client for the host clue-rating control-plane write (#201, FR-11.x). A rating is a
 * private, host-attributed annotation on a completed clue's quality — it rides the HTTP
 * control plane (`POST /api/clue-ratings`), NOT the game WebSocket (D201.2), so submitting
 * one never touches the buzz budget and carries no protocol-version weight.
 *
 * Mirrors {@link ./credits-api} (`redeemCode`): validate inline via the shared authority
 * first (so an obviously-bad payload never leaves the browser), then POST. Host authority
 * is proven by the host `ticket`; the rater identity is resolved SERVER-SIDE from the
 * signed `guestToken` (never client-trusted — FR-9.3). 413/422/429/403 map to friendly copy.
 */

import {
  validateClueRating,
  validateClueRatingRetraction,
  type ClueRatingRetraction,
  type ClueRatingSubmission,
} from '@jeopardy/shared';
import { SERVER_HTTP_URL } from '../config';
import { ApiError } from './api-error';

/** Friendly copy per rating-submission failure status (FR-9.5). */
const RATE_FAILURE_MESSAGES: Readonly<Record<number, string>> = {
  403: 'Only the game host can rate clues.',
  413: 'That rating was too large to send.',
  422: 'That rating looks incomplete — please try again.',
  429: 'You’re rating a little fast — give it a moment.',
};

/**
 * Submit a host clue rating. Validates the content inline (throwing an {@link ApiError}
 * with status 422 on a client-detectable problem, so we never POST a doomed payload), then
 * sends it with the host ticket + the host's signed guest identity. Resolves on a 2xx
 * accept; throws an {@link ApiError} carrying a friendly message otherwise.
 */
export async function rateClue(
  submission: ClueRatingSubmission,
  guestToken: string,
): Promise<void> {
  const validation = validateClueRating(submission);
  if (!validation.ok) {
    throw new ApiError(validation.error, 422);
  }
  const res = await fetch(`${SERVER_HTTP_URL}/api/clue-ratings`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...submission, guestToken }),
  });
  if (!res.ok) {
    const message =
      RATE_FAILURE_MESSAGES[res.status] ??
      'Could not save that rating. Please try again.';
    throw new ApiError(message, res.status);
  }
}

/**
 * Retract ("Undo") a rating the host just recorded, within the brief post-save window
 * (#201, FR-11.x). Validates the key inline (throwing a 422 {@link ApiError} on a client-
 * detectable problem, so we never send a doomed request), then issues a `DELETE
 * /api/clue-ratings` with the host ticket + the host's signed guest identity — the server
 * resolves the rater from the token, so this can only remove the host's own row. Resolves
 * on a 2xx accept; throws an {@link ApiError} with a friendly message otherwise.
 */
export async function unrateClue(
  retraction: ClueRatingRetraction,
  guestToken: string,
): Promise<void> {
  const validation = validateClueRatingRetraction(retraction);
  if (!validation.ok) {
    throw new ApiError(validation.error, 422);
  }
  const res = await fetch(`${SERVER_HTTP_URL}/api/clue-ratings`, {
    method: 'DELETE',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...retraction, guestToken }),
  });
  if (!res.ok) {
    const message =
      RATE_FAILURE_MESSAGES[res.status] ??
      'Could not undo that rating. Please try again.';
    throw new ApiError(message, res.status);
  }
}
